/* Plantica — Cart count

   One element for both places the cart is counted: the bubble on the header's
   cart icon, and „N produse" in the drawer head.

   It replaces the theme's <cart-count> in the header because that one prints
   `item_count` as it is, and `item_count` includes the order cover. The cover
   is a real product and stays in the cart and at checkout — it is only left out
   of the number, since the customer did not pick it and „2 produse" for one
   shampoo reads as a mistake.

   Two forms, told apart by the markup:

     data-one / data-many   the drawer head: a number with its noun. In Romanian
                            the noun changes with the number — „1 produs",
                            „3 produse", „20 de produse" — and both words come
                            from the section settings.
     neither                the header bubble: a bare number, „99+" past 99,
                            exactly as the theme's element draws it, including
                            the `cart-has-items` class on <html>.

   `data-cover-product` is the cover's product id, printed by Liquid from Theme
   settings → Cart. Every line of that product is subtracted; nothing else is.
   The first render comes from snippets/plantica-cart-counted.liquid, which
   applies the same rule, so the number does not change when this takes over.

   Loaded by the header and by the cart drawer; the guard below makes the
   second load a no-op.

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  if (customElements.get('plantica-cart-count')) return;

  /* `item_count` less the cover's lines. A payload without `items` cannot be
     corrected, and is shown as it came rather than guessed at — every cart
     update the theme publishes carries the full cart, so this is a fallback,
     not a path. */
  const counted = (cart, cover) => {
    const items = cart.items;
    if (!cover || !Array.isArray(items)) return cart.item_count;

    return items.reduce(
      (count, item) => (Number(item.product_id) === cover ? count - item.quantity : count),
      cart.item_count
    );
  };

  class PlanticaCartCount extends HTMLElement {
    connectedCallback() {
      const theme = window.FoxTheme;
      if (!theme || !theme.pubsub) return;

      this.unsubscribe = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.onCartUpdate.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    get cover() {
      return Number(this.dataset.coverProduct) || 0;
    }

    onCartUpdate(event) {
      if (!event || !event.cart || event.cart.errors) return;

      const count = Math.max(0, counted(event.cart, this.cover));

      if ('many' in this.dataset) this.drawWords(count);
      else this.drawBubble(count);
    }

    drawWords(count) {
      this.hidden = count === 0;
      if (count === 0) return;

      this.textContent = `${count} ${this.noun(count)}`;
    }

    drawBubble(count) {
      if (count > 99) {
        this.innerHTML = '<span class="text-sm">99+</span>';
      } else {
        this.textContent = count;
      }

      this.classList.toggle('cart-count--small-medium', count > 99);
      this.hidden = count === 0;
      document.documentElement.classList.toggle('cart-has-items', count > 0);
    }

    /* Romanian counts: 1 takes the singular; the plural needs "de" when the
       last two digits are 00 or 20–99 — 3 produse, 20 de produse, 101 produse,
       120 de produse. */
    noun(count) {
      const one = this.dataset.one || 'produs';
      const many = this.dataset.many || 'produse';
      const rest = count % 100;

      if (count === 1) return one;
      return rest === 0 || rest >= 20 ? `de ${many}` : many;
    }
  }

  customElements.define('plantica-cart-count', PlanticaCartCount);
})();
